import { type z } from 'zod'
import { reportError } from '@mc/next-newrelic'
import {
  BannerBlockSchema,
  CardBlockSchema,
  CtaBlockSchema,
  FooterBlockSchema,
  ModalBlockSchema,
  type ProductSurfaceCopySchema,
  type SurfaceOverridesSchema,
} from './schemas'

// The two orthogonal axes. `surface` = where the copy renders; release state
// (`comingSoon`) = launched vs not-yet. cert reads the
// CertificatePromoVariables payload; subscription has its own narrower payload
// (SubscriptionPromoVariables) but both are structurally compatible with
// SurfaceCopyBase below, so the same merge logic serves both.
export type ProductSurface = 'cert' | 'subscription'

export type ResolveTarget = {
  surface: ProductSurface
  comingSoon?: boolean
  preEnroll?: boolean
}

// The structural shape every productSurface-bearing payload shares: a set of
// optional copy blocks. CertificatePromoVariables declares all five;
// SubscriptionPromoVariables only ever populates banner/footer, which is fine —
// every field here is optional, so it's still structurally assignable.
type SurfaceCopyBase = Partial<{
  card: z.infer<typeof CardBlockSchema>
  banner: z.infer<typeof BannerBlockSchema>
  modal: z.infer<typeof ModalBlockSchema>
  cta: z.infer<typeof CtaBlockSchema>
  footer: z.infer<typeof FooterBlockSchema>
}>

type CopyOverrides = z.infer<typeof ProductSurfaceCopySchema>

// The structural shape applyProductSurface (and resolvePromoCopy, which just
// forwards to it) accepts: a SurfaceCopyBase plus its productSurface map.
// Named once here rather than derived via `Parameters<typeof
// applyProductSurface>[0]` at the call site, which is indirect and would
// silently change meaning if the generic's constraint were ever reordered or
// a second parameter added.
export type SurfaceCopyPayload = SurfaceCopyBase & {
  productSurface?: Partial<
    Record<ProductSurface, z.infer<typeof SurfaceOverridesSchema>>
  >
}

// Merge a partial override block over the default block, keeping the result only
// when it is a COMPLETE block per the block's canonical schema. The override
// supplies a subset of fields and the default supplies the rest, so the union is
// usually complete — including when the default omits the block entirely and the
// override covers every required field (a `card` is just `badgeText`). An
// incomplete merge is dropped rather than rendered broken (a `modal` with no
// default would be missing its required image_16x9/body, an override with a bad
// `buttonKind` enum fails the same way). Validating the RESULT, not the presence
// of a default, is what keeps a new surface override from silently no-oping
// because the default payload happens to omit that block.
//
// A drop is invisible in the page (generic copy renders, price/coupon stay
// correct), so it is reported — the same zod-error-to-New-Relic gate the promo
// fetchers/hook use on the payload itself, here for the merge.
//
// Deliberately a shallow spread, NOT lodash `_.merge`: block `text` is a
// StyledVariable that can be an ARRAY of segments, and an override must REPLACE
// it wholesale. `_.merge` deep-merges arrays by index (['A','B'] over ['A2'] →
// ['A2','B'], leaking the stale second segment) and mutates its first arg. The
// spread gives the correct replace-per-field semantics, immutably.
const mergeBlock = <T extends object>(
  block: string,
  schema: z.ZodType<T>,
  base: T | undefined,
  override: Partial<T> | undefined,
): T | undefined => {
  if (!override) return base
  const parsed = schema.safeParse({ ...base, ...override })
  if (parsed.success) return parsed.data

  reportError(parsed.error, {
    message: 'Promo productSurface override dropped: incomplete merged block',
    block,
    override: JSON.stringify(override),
  })
  return base
}

// Layer one override set over the running copy. Blocks are spread only when
// defined, keeping the key set identical to the no-override path (no explicit
// `key: undefined` in the props). The `as T` is safe: the object literal only
// ever (re)sets keys T already declares as optional (SurfaceCopyBase).
const applyOverride = <T extends SurfaceCopyBase>(
  base: T,
  override: CopyOverrides | undefined,
): T => {
  if (!override) return base
  const card = mergeBlock('card', CardBlockSchema, base.card, override.card)
  const banner = mergeBlock(
    'banner',
    BannerBlockSchema,
    base.banner,
    override.banner,
  )
  const modal = mergeBlock(
    'modal',
    ModalBlockSchema,
    base.modal,
    override.modal,
  )
  const cta = mergeBlock('cta', CtaBlockSchema, base.cta, override.cta)
  const footer = mergeBlock(
    'footer',
    FooterBlockSchema,
    base.footer,
    override.footer,
  )
  return {
    ...base,
    ...(card && { card }),
    ...(banner && { banner }),
    ...(modal && { modal }),
    ...(cta && { cta }),
    ...(footer && { footer }),
  } as T
}

/**
 * Resolve the promo copy for a surface + release state by layering the site-wide
 * payload's `productSurface` overrides over the default copy blocks, then
 * dropping `productSurface` from the result. Cascade, most-general first:
 *
 *   default  ←  productSurface[surface].default  ←  productSurface[surface].comingSoon  ←  productSurface[surface].preEnroll
 *
 * so a launched surface gets `default`; a coming-soon surface (plain or
 * pre-enroll) also gets `comingSoon`; a pre-enroll surface additionally gets
 * `preEnroll` layered on top. Callers pass `comingSoon: true` for BOTH plain
 * coming-soon and pre-enroll products, so a `preEnroll` override without a
 * matching `comingSoon` override still inherits the coming-soon copy rather
 * than reverting to `default`. Each layer is a per-block shallow merge
 * (overriding one field keeps the rest), kept only when the merged block is
 * complete per its canonical schema. A payload without `productSurface` (e.g.
 * a per-slug promo), or a surface with no overrides, returns the default copy
 * unchanged.
 */
export const applyProductSurface = <T extends SurfaceCopyPayload>(
  vars: T,
  { surface, comingSoon, preEnroll }: ResolveTarget,
): Omit<T, 'productSurface'> => {
  const { productSurface, ...base } = vars
  const overrides = productSurface?.[surface]
  if (!overrides) return base
  const launched = applyOverride(base, overrides.default)
  const comingSoonCopy = comingSoon
    ? applyOverride(launched, overrides.comingSoon)
    : launched
  return preEnroll
    ? applyOverride(comingSoonCopy, overrides.preEnroll)
    : comingSoonCopy
}
